import { Alert, Box, Button, Header, Input, SegmentedControl, SpaceBetween, Spinner } from "@amzn/awsui-components-react";
import { SelectProps } from "@amzn/awsui-components-react/polaris/select";
import { getString } from "../../../common/ui-string-labels/ui-strings-utils";
import { PROD_DOMAIN } from "../../../configuration/config";

import { useFlashBarItemsActions } from "common/UseFlashBarItems/useFlashBarItems";
import { AssignOrderModal } from "../AssignOrderModal";
import React, { useState } from "react";
import { SelectionState } from "../useManageOrdersTable";
import { GetOrders_AllCommandOutput, Order } from "@amzn/ito-client";
import useOpenNewTab from "hooks/useOpenNewTab";
import { getITOClient } from "hooks/hooks-helpers";
import { AMZNHAM_CONSUMER } from "interfaces/constants";
import { MANAGE_ORDER_TABS } from "../../../../src/interfaces/hardware-models";
import { PERMISSIONS } from "../../../configuration/config";
import { UserPermissions } from "services/user-permissions";

const getManageOrdersTabsConfig = (showSupportTab: boolean) => {

    return [
        { id: MANAGE_ORDER_TABS.ALL_HAM_ORDERS, text: "All HAM Orders" },
        { id: MANAGE_ORDER_TABS.ITSE_HAM_ORDERS, text: "ITSE HAM Orders" },
        { id: MANAGE_ORDER_TABS.AMAZONIAN_HAM_ORDERS, text: "Amazonian HAM Orders" },
        // only include support-requested tab if is not in production (for testing)
        ...(showSupportTab
            ? [{
                id: MANAGE_ORDER_TABS.IT_SHOP_ORDERS_SUPPORT_REQUESTED,
                text: "Order Support for IT Shop Orders",
            }]
            : []),
    ];
};

export const ManageOrdersTableHeader = (props: {
    totalResults: number;
    selectedItems: Order[];
    onOrderTypeTabChange: (newlySelectedOrderTypeTab: string) => void;
    procurementMembersSelectOptions: SelectProps.Option[];
    refreshData: (clearPreviousData: boolean) => void;
    selectedOrderTypeTab: string;
    useFlashBarItemsActions: useFlashBarItemsActions;
}): JSX.Element => {
    /* istanbul ignore next */
    const [showAssignModal, setShowAssignModal] = useState(false);
    const { useFlashBarItemsActions, onOrderTypeTabChange, procurementMembersSelectOptions, selectedOrderTypeTab } = props;

    const [searchValue, setSearchValue] = useState("");
    const [showNotFound, setShowNotFound] = useState(false);
    const [isLoadingAmazonianOrder, setIsLoadingAmazonianOrder] = useState(false);
    const openNewTab = useOpenNewTab();

    let selectionState: SelectionState;

    /* istanbul ignore next */
    if (props.selectedItems.length == 0) {
        selectionState = SelectionState.NONE;
    } else if (props.selectedItems.length == 1) {
        selectionState = SelectionState.ONE;
    } else {
        selectionState = SelectionState.MULTIPLE;
    }

    const handleSearch = async (e: React.FormEvent<HTMLElement>) => {
        e.preventDefault();

        const orderReference = searchValue;

        if (!orderReference) return;

        console.log("orderReference", orderReference);

        const { client } = getITOClient(AMZNHAM_CONSUMER);
        const request = { filters: { orderReference: [orderReference] }, pageSize: 1, nextToken: "0" };

        setIsLoadingAmazonianOrder(true);
        client.getOrders_All(request, {}, (err: any, response?: GetOrders_AllCommandOutput) => {
            console.log(`response ${JSON.stringify(response)}`);

            if (!response || err || !response.orders || response.totalResults === 0) {
                setShowNotFound(true);
                console.log("Order not found");
            } else {
                console.log("Order found");
                const currentLocation = window.location.origin;
                const url = `${currentLocation}/orders/amznham-${orderReference}`;

                openNewTab(url);
            }
            setIsLoadingAmazonianOrder(false);
        });
    };

    /* istanbul ignore next */
    const onAssignOrders = () => {
        setShowAssignModal(true);
    };

    /* istanbul ignore next */
    const dismissAssignModal = (shouldRefreshTable: boolean = false) => {
        if (shouldRefreshTable) {
            props.refreshData(true);
        }
        setShowAssignModal(false);
    };

    /* istanbul ignore next */
    const disableAssigneeButton = (): boolean => {
        if (selectionState == SelectionState.NONE) return true;

        return false;
    };

    const separatorPipe = () => (
        <div style={{ display: "flex", alignItems: "center", margin: "0 8px" }}>
            <div style={{ height: "24px", width: "1px", backgroundColor: "#d5dbdb" }}></div>
        </div>
    );

    const wrappedSpinner = () => (
        <div data-testid="searching-amazonian-order-spinner">
            <Spinner size="normal" />
        </div>
    );

    const amazonianOrderSearch = () => {
        return (
            <>
                {isLoadingAmazonianOrder && wrappedSpinner()}
                <form onSubmit={(e) => handleSearch(e)}>
                    <Input
                        onChange={(event) => setSearchValue(event.detail.value)}
                        value={searchValue}
                        placeholder={getString("manageOrders.amazonianOrderSearch.placeholder")}
                        data-testid="amazonian-order-search-input"
                    />
                </form>
            </>
        );
    };

    const amazonianOrderNotFound = () => {
        return (
            <Alert
                dismissible
                type="error"
                header={getString("manageOrders.amazonianOrderSearch.amazonianOrderNotFound.header")}
                onDismiss={() => setShowNotFound(false)}
                data-testid="amazonian-order-not-found-alert"
            >
                {getString("manageOrders.amazonianOrderSearch.amazonianOrderNotFound.message")}
            </Alert>
        );
    };



    // Feature flag to show Hardware Order Support option
    const showSupportTab = UserPermissions.instance.hasPermission(PERMISSIONS.SHOW_ORDER_SUPPORT_TAB);

    return (
        <SpaceBetween size="m">
            <Header
                data-testid="manage-orders-table-header"
                variant="awsui-h1-sticky"
                counter={`(${props.totalResults})`}
                actions={
                    <SpaceBetween direction="horizontal" size="xs">
                        {amazonianOrderSearch()}
                        {separatorPipe()}
                        <Button
                            data-testid="open-assign-modal-button"
                            variant="normal"
                            disabled={disableAssigneeButton()}
                            onClick={onAssignOrders}
                        >
                            {getString("manageOrders.table.assignOrderButton")}
                        </Button>
                    </SpaceBetween>
                }
            >
                {getString("manageOrders.table.headerTitle")}
            </Header>
            {showAssignModal && (
                <AssignOrderModal
                    onDismiss={dismissAssignModal}
                    orders={props.selectedItems}
                    flashBarItemsActions={useFlashBarItemsActions!}
                    procurementMembersSelectOptions={procurementMembersSelectOptions}
                />
            )}
            {showNotFound && amazonianOrderNotFound()}
            <SegmentedControl
                data-testid="segmented-tabs-id"
                selectedId={selectedOrderTypeTab}
                onChange={({ detail }) =>
                    onOrderTypeTabChange(detail.selectedId)
                }
                label="Manage Order Tabs"
                options={getManageOrdersTabsConfig(showSupportTab).map(orderTabConfig => orderTabConfig)}
            />
            <Box color="text-status-inactive">{getString("manageOrders.table.headerDescription")}</Box>
        </SpaceBetween>
    );
};
